import React, {
	useCallback,
	useEffect,
	type KeyboardEvent,
	memo,
	useImperativeHandle,
} from 'react';
import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import RecentIcon from '@atlaskit/icon/core/migration/clock--recent';
import type { TriggerProps } from '@atlaskit/popup';
import { Flex, xcss, Text } from '@atlaskit/primitives';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { PopupComponent } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { useDateTimeFormatter } from '@atlassian/jira-issue-format-date/src/main.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import { EmptyCellContent, Container } from '../../../common/ui/list-cell/styled.tsx';
import DateTimeFieldEditView from './async-date-time-picker/index.tsx';
import messages from './messages.tsx';
import { CellContent } from './styled.tsx';
import type { DateTimeCellProps } from './types.tsx';

const POPUP_OFFSET: [number, number] = [0, -DEFAULT_ROW_HEIGHT - 1];

const DateTimeCell = memo(
	({
		issueKey = '',
		fieldKey,
		fieldValue = null,
		width,
		height,
		isSelected = false,
		editMode = false,
		setEditMode = noop,
		isHovering = false,
		cellRef,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		inputValue,
		onInputChange = noop,
	}: DateTimeCellProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();

		const textInputValue = (inputValue ?? fieldValue) || '';
		const setTextInputValue = useCallback(
			(value: string | null) => onInputChange(value === null ? '' : value),
			[onInputChange],
		);

		const { formatDateTime } = useDateTimeFormatter(null);
		const [{ value, error }, { saveValue, resetError }] = useEditField<string | null, null>({
			fieldKey,
			issueKey,
			fieldType: 'datetime',
			initialValue: fieldValue,
		});

		useEffect(() => {
			setTextInputValue(value);
		}, [setTextInputValue, value]);

		const handleChange = useCallback(
			(newValue: string | null) => {
				resetError();
				if (newValue === value) {
					return;
				}
				saveValue(newValue, null, createAnalyticsEvent({}));
			},
			[createAnalyticsEvent, resetError, saveValue, value],
		);

		const saveAndClose = useCallback(() => {
			setEditMode(false);
			if (textInputValue === value) {
				return;
			}
			saveValue(textInputValue, null, createAnalyticsEvent({}));
		}, [createAnalyticsEvent, saveValue, textInputValue, value, setEditMode]);

		const staticContent = textInputValue !== null && textInputValue !== '' && (
			<Container>
				<CellContent>{formatDateTime(textInputValue)}</CellContent>
			</Container>
		);

		const handleKeyDown = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && ['Enter', 'Tab'].includes(event.code)) {
					return true;
				}
				return false;
			},
			[editMode],
		);

		const popupContent = useCallback(
			() => (
				<DateTimeFieldEditView
					width={width}
					height={height}
					value={textInputValue}
					onChange={handleChange}
				/>
			),
			[handleChange, height, textInputValue, width],
		);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<div {...triggerProps}>
					<Container>
						{textInputValue !== null && textInputValue !== '' && (
							<CellContent>{formatDateTime(textInputValue)}</CellContent>
						)}
						{(textInputValue === null || textInputValue === '') && (
							<EmptyCellContent>
								<RecentIcon spacing="spacious" label={formatMessage(messages.emptyDate)} />
								<Text>{formatMessage(messages.emptyDate)}</Text>
							</EmptyCellContent>
						)}
					</Container>
				</div>
			),
			[formatDateTime, formatMessage, textInputValue],
		);

		useImperativeHandle(cellRef, () => ({ onKeyDown: handleKeyDown }), [handleKeyDown]);

		const cellContent = (
			<>
				{isHovering || editMode || isSelected ? (
					<Popup
						autoFocus={false}
						isOpen={editMode}
						placement="bottom-start"
						messageId="business-list.ui.list-view.date-time-cell.popup"
						messageType="transactional"
						offset={POPUP_OFFSET}
						popupComponent={PopupComponent}
						onClose={saveAndClose}
						content={popupContent}
						trigger={popupTrigger}
					/>
				) : (
					staticContent
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		return (
			<Flex
				xcss={[wrapperStyles]}
				testId="business-list.ui.list-view.date-time-cell.cell-container"
			>
				{cellContent}
			</Flex>
		);
	},
);

export default DateTimeCell;

const wrapperStyles = xcss({
	width: '100%',
	height: '100%',
});
